import {
  API_B2C_DEVELOPMENT_BASE_URL,
  API_B2C_ORDERS_DOCUMENT_DOWNLOAD_PATH,
  API_B2C_PRODUCTION_BASE_URL,
  API_B2C_STAGING_BASE_URL,
} from '~/constants';
import { getAppEnv } from '~/constants/env';
import { Logger } from '~/lib/logger';
import type { B2COrderItemStatusType } from '~/types/B2COrdersData';
import { B2CApiResponseSchema } from '~/types/B2COrdersData';
import type {
  OrdersB2BResponseDataItemType,
  OrdersResponseDataType,
  OrdersTableDataType,
  OrdersTableStatusType,
  TableOrder,
} from '~/types/OrdersTableData';
import { isB2BResponse } from '~/types/OrdersTableData';
import type { StatusMapping } from '~/types/table';

const logger = new Logger('Table Utility Functions');

/**
 * Compare two items for sorting in descending order
 * @param a - the first item to compare
 * @param b - the second item to compare
 * @param orderBy - the property to order by
 * @returns - a number indicating the order of the items
 */
export const descendingComparator = <T>(a: T, b: T, orderBy: keyof T) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

/**
 * Get a comparator function for use with a table
 * @param order - the order of the table
 * @param orderBy - the property to order by
 * @returns - a comparator function
 */
export const getComparator = <T>(order: TableOrder, orderBy: keyof T) => {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
};

/**
 * Convert the response data from the warehouse API to a consistent format suitable for the table
 * @param responseData - the response data from the warehouse API (B2B or B2C)
 * @returns - the formatted table data
 */
export const convertToTableData = (responseData: OrdersResponseDataType): OrdersTableDataType | null => {
  const b2cParsedData = B2CApiResponseSchema.safeParse(responseData);
  const appEnv = getAppEnv();
  const baseB2CApiUrl =
    appEnv === 'production'
      ? API_B2C_PRODUCTION_BASE_URL
      : appEnv === 'staging'
        ? API_B2C_STAGING_BASE_URL
        : API_B2C_DEVELOPMENT_BASE_URL;
  const documentDownloadPath = `${baseB2CApiUrl}${API_B2C_ORDERS_DOCUMENT_DOWNLOAD_PATH}`;

  /*
    We use a type guard for the B2B response because it comes from an api which already does the zod validation as part of the contract. For B2C, the api doesn't use a contract, so we need to validate / narrow the type for the response data using zod.
  */
  const isB2B = isB2BResponse(responseData);
  if (isB2B) {
    return {
      type: 'b2b',
      rows: responseData.data.map((item) => {
        const { label: statusLabel } = getTableStatus(item.status);
        return {
          id: item.id,
          product: item.product.name,
          sku: item.product.sku,
          quantity: item.quantity,
          orderDate: item.createdDate,
          pharmacy: item.pharmacy.name ?? '',
          address: item.pharmacy.address,
          contact: item.pharmacy.phone ?? '',
          approval: item.approval
            ? {
                id: item.approval.code ?? '',
                documentPath: item.approval.documentPath ?? '',
              }
            : undefined,
          doctor: item.approval?.doctorName ?? '',
          status: statusLabel,
          dispatch: null,
        };
      }),
    };
  } else if (b2cParsedData.success) {
    return {
      type: 'b2c',
      rows: b2cParsedData.data.data.list.map((item) => {
        const { name: pharmacistName, phone: pharmacistPhone, address1, address2 } = item.Order.Pharmacist.Pharmacy;
        const { label: statusLabel } = getB2CTableStatus(item.OrderItemStatus);

        return {
          id: item.id.toString(),
          product: item.Product.name,
          sku: item.Product.sku_code,
          quantity: item.quantity,
          orderDate: item.created_date,
          pharmacy: pharmacistName,
          address: [address1, address2].filter(Boolean).join(', '),
          contact: pharmacistPhone ?? undefined,
          approval: {
            id: item.ApprovalDocument?.id.toString() ?? '',
            documentPath: item.ApprovalDocument?.document_path
              ? `${documentDownloadPath}/${item.ApprovalDocument?.document_path}`
              : '',
          },
          doctor: item.ApprovalDocument?.doctor_name,
          status: statusLabel,
          dispatch: item.ConsignmentOrdersItem?.Consignment ? 'Yes' : '-',
        };
      }),
    };
  }

  if (!isB2B && b2cParsedData.error) {
    logger.error('Error parsing B2C response data', b2cParsedData.error.errors);
  }
  return null;
};

/**
 * Get the status to send to the API based on the status of the orders table
 * @param status - the status of the orders table
 * @returns - the status to send to the API
 */
export const getApiStatus = (status: OrdersTableStatusType): OrdersB2BResponseDataItemType['status'][] => {
  switch (status) {
    case 'awaitingApproval':
      return ['AWAITING_SUPPLIER_APPROVAL'];
    case 'rejected':
      return ['REJECTED_BY_SUPPLIER'];
    case 'approved':
      return ['APPROVED_BY_SUPPLIER', 'AWAITING_SHIPMENT', 'RECEIVED', 'RECEIVED_WITH_DISPUTE', 'COMPLETED'];
    default:
      return [
        'AWAITING_SUPPLIER_APPROVAL',
        'APPROVED_BY_SUPPLIER',
        'REJECTED_BY_SUPPLIER',
        'AWAITING_SHIPMENT',
        'RECEIVED',
        'RECEIVED_WITH_DISPUTE',
        'COMPLETED',
      ];
  }
};

/**
 * Get the status value and label for the table based on the status from the API
 * @param status - the status from the API
 * @returns - an object containing the status value and label for the table
 */
export const getTableStatus = (status: OrdersB2BResponseDataItemType['status']): StatusMapping => {
  switch (status) {
    case 'AWAITING_SUPPLIER_APPROVAL':
      return { value: 'awaitingApproval', label: 'Awaiting Approval' };
    case 'REJECTED_BY_SUPPLIER':
      return { value: 'rejected', label: 'Rejected' };
    case 'APPROVED_BY_SUPPLIER':
    case 'AWAITING_SHIPMENT':
    case 'RECEIVED':
    case 'RECEIVED_WITH_DISPUTE':
    case 'COMPLETED':
      return { value: 'approved', label: 'Approved' };
    default:
      return { value: 'all', label: 'All' };
  }
};

/**
 * Get the status value and label for the table based on the status from the B2C API.
 * This is a workaround since the B2C API does not return the status in the same format as the B2B API. This forces the status to be consistent across both APIs. This should be refactored once the backend is made consistent across both APIs.
 * @param status - the status from the B2C API
 * @returns - an object containing the status value and label for the table
 */
export const getB2CTableStatus = (status: B2COrderItemStatusType): StatusMapping => {
  switch (status.id) {
    case 1:
    case 10:
    case 11:
    case 14:
      return { value: 'awaitingApproval', label: 'Awaiting Approval' };
    case 2:
    case 3:
    case 4:
    case 8:
    case 9:
    case 12:
      return { value: 'approved', label: 'Approved' };
    case 5:
    case 6:
    case 7:
    case 13:
    case 15:
      return { value: 'rejected', label: 'Rejected' };
    default:
      return { value: 'all', label: 'All' };
  }
};

/**
 * Convert the human-readable table status to an array of B2C status IDs for filtering
 * @param status - the human-readable status from the table
 * @returns - an array of B2C status IDs
 */
export const mapTableStatusToB2CStatusIds = (status: OrdersTableStatusType): B2COrderItemStatusType['id'][] => {
  switch (status) {
    case 'awaitingApproval':
      return [1, 10, 11, 14];
    case 'approved':
      return [2, 3, 4, 8, 9, 12];
    case 'rejected':
      return [5, 6, 7, 13, 15];
    default:
      return []; // Return an empty array to indicate "all" statuses
  }
};
